import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import worldMap from '../assets/images/world-map.png';
import usaFlag from '../assets/images/usa.png';
import nlFlag from '../assets/images/nl.png';
import {
  FiCopy,
  FiRefreshCw,
  FiPower,
  FiClock,
  FiArrowUp,
  FiSettings,
  FiCheck,
  FiX,
} from 'react-icons/fi';

import usa from "../../src/assets/images/usa.png"
import nl from "../../src/assets/images/nl.png"
import priceBG from "../../src/assets/images/price-bg.png";

import shutdown from '../../src/assets/images/shutdown.png';
import upgrade from '../../src/assets/images/upgrade.png';
import restart from '../../src/assets/images/restart.png';
import reinstallation from '../../src/assets/images/reinstallation.png';
import extend from '../../src/assets/images/extend.png';
import { CUSTOM_PRICING, TIER_TO_ACERDP, ACERDP_NAME_TO_TIER, resolveTierMonthlyPriceEUR, formatTierMonthlyPriceDisplay } from '../data/pricing';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ServerManagement = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const [server, setServer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [extendDays, setExtendDays] = useState(30);
  const [osTemplates, setOsTemplates] = useState([]);
  const [showReinstallModal, setShowReinstallModal] = useState(false);
  const [selectedOsId, setSelectedOsId] = useState('');
  const [plans, setPlans] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeDays, setUpgradeDays] = useState(30);
  const [selectedUpgradePlanId, setSelectedUpgradePlanId] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    type: '',
    payload: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  /** Fewer bar segments on narrow screens so usage cards never overflow horizontally */
  const [usageBarSegments, setUsageBarSegments] = useState(22);

  useEffect(() => {
    fetchServer();
    fetchOSTemplates();
  }, [id]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const sync = () => setUsageBarSegments(mq.matches ? 14 : 22);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const fetchServer = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/servers/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServer(response.data.server);
    } catch (error) {
      toast.error('Failed to fetch server details');
      navigate('/services');
    } finally {
      setLoading(false);
    }
  };

  const fetchOSTemplates = async () => {
    try {
      const response = await axios.get(`${API_URL}/servers/config`);
      setOsTemplates(response.data.templates || []);
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to fetch OS templates:', error);
    }
  };

  const copyToClipboard = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    window.setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 900);
    toast.success('Copied to clipboard!');
  };

  const handleAction = async (action, data = {}) => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/servers/${id}/${action}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Server ${action} initiated successfully`);
      fetchServer();
      if (user) fetchUser();
    } catch (error) {
      toast.error(
        error.response?.data?.message || `Failed to ${action} server`,
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirmModal = (title, message, type, payload = null) => {
    setConfirmModal({
      open: true,
      title,
      message,
      type,
      payload,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      open: false,
      title: '',
      message: '',
      type: '',
      payload: null,
    });
  };

  const handleExtend = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/servers/${id}/extend`,
        { days: extendDays },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Server extended by ${extendDays} days`);
      setShowExtendModal(false);
      fetchServer();
      if (user) fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to extend server');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReinstall = async () => {
    if (!selectedOsId) {
      toast.error('Please select an operating system');
      return;
    }
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/servers/${id}/reinstall`,
        { osId: selectedOsId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Server reinstallation initiated');
      setShowReinstallModal(false);
      fetchServer();
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to reinstall server',
      );
    } finally {
      setActionLoading(false);
    }
  };

  const toggleAutoRenew = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/servers/${id}/auto-renew`,
        { enabled: !server.autoRenew },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setServer(response.data.server);
      toast.success('Auto-renew updated');
    } catch (error) {
      toast.error('Failed to update auto-renew');
    }
  };

  const getPlanMonthlyPrice = (plan) => {
    const locationKey =
      (server?.location || '').toLowerCase() === 'nl' ? 'nl' : 'us';
    const price =
      plan?.price ?? plan?.pricing?.[locationKey] ?? plan?.pricing?.us ?? 0;
    return Number(price) || 0;
  };

  const handleUpgrade = async () => {
    if (!selectedUpgradePlanId) {
      toast.error('Please select a target package');
      return;
    }

    const acerdpPackageId = TIER_TO_ACERDP[selectedUpgradePlanId];
    if (!acerdpPackageId) {
      toast.error('Invalid package mapping');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/servers/${id}/upgrade`,
        { packageId: acerdpPackageId, days: upgradeDays },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Server upgrade initiated');
      setShowUpgradeModal(false);
      fetchServer();
      if (user) fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upgrade server');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmedAction = async () => {
    const { type, payload } = confirmModal;

    if (type === 'reset-password') {
      setPasswordResetLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API_URL}/servers/${id}/change-password`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const pw = res.data?.password;
        if (pw) {
          setServer((prev) =>
            prev ? { ...prev, password: pw, credentials: { ...prev.credentials, password: pw } } : prev
          );
        } else {
          await fetchServer();
        }
        toast.success(res.data?.message || 'Password reset successfully');
        setShowPassword(true);
        closeConfirmModal();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to reset password');
      } finally {
        setPasswordResetLoading(false);
      }
      return;
    }

    closeConfirmModal();

    if (type === 'server-action' && payload) {
      await handleAction(payload.action, payload.data || {});
      return;
    }

    if (type === 'extend') {
      await handleExtend();
      return;
    }

    if (type === 'reinstall') {
      await handleReinstall();
      return;
    }

    if (type === 'upgrade') {
      await handleUpgrade();
      return;
    }

    if (type === 'toggle-auto-renew') {
      await toggleAutoRenew();
      return;
    }
  };

  if (loading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  if (!server) return null;

  const mergedPassword =
    server.password ||
    server.credentials?.password ||
    server.credentials?.data?.password ||
    server.acerdpData?.credentials?.password;

  const displayIp =
    server.ip ||
    server.acerdpData?.credentials?.ip ||
    server.credentials?.ip ||
    server.acerdpData?.ip ||
    'N/A';

  const displayUsername =
    server.username ||
    server.acerdpData?.credentials?.username ||
    server.credentials?.username ||
    'Administrator';

  const displayStatus = String(
    server.status ||
    server.acerdpData?.status ||
    'active'
  );

  const powerStatus = server.acerdpData?.power_status || 'running';
  const isStopped = powerStatus === 'stopped' || powerStatus === 'off';

  // Calculate extend price
  const extendPrice = (server.monthlyPrice * extendDays) / 30;

  // Current server's custom tier (match by AcerDP plan name or specs)
  const currentTierId = ACERDP_NAME_TO_TIER[server.plan] || '';
  const currentTierIdx = CUSTOM_PRICING.tiers.findIndex(t => t.id === currentTierId);
  // Only show tiers higher than current
  const upgradeTiers = CUSTOM_PRICING.tiers.filter((_, i) => i > currentTierIdx);
  const selectedUpgradeTier = CUSTOM_PRICING.tiers.find(t => t.id === selectedUpgradePlanId);
  const upgradePrice =
    ((resolveTierMonthlyPriceEUR(selectedUpgradeTier) || 0) * upgradeDays) / 30;

  const locationName =
    server.location?.toLowerCase() === 'nl' ? 'Netherlands' : 'USA';
  const locationIcon =
    server.location?.toLowerCase() === 'nl' ? nlFlag : usaFlag;

  const toNum = (v, fallback = 0) => {
    if (v === null || v === undefined) return fallback;
    if (typeof v === 'number') return Number.isFinite(v) ? v : fallback;
    if (typeof v === 'string') {
      // handle values like "45%", "1,024", "512 MB"
      const cleaned = v.replace(/,/g, '');
      const match = cleaned.match(/-?\d+(\.\d+)?/);
      if (!match) return fallback;
      const n = Number(match[0]);
      return Number.isFinite(n) ? n : fallback;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const clamp = (n, min = 0, max = 100) => Math.min(max, Math.max(min, n));

  const stats = server.acerdpData?.stats || {};
  const cpuRaw =
    stats.cpu ??
    stats.cpu_usage ??
    stats.cpu_percent ??
    stats.cpu_percentage ??
    stats.cpu_usage_percentage ??
    stats.cpuPct ??
    server.acerdpData?.cpu ??
    server.acerdpData?.cpu_usage ??
    server.acerdpData?.cpu_percent ??
    null;
  const cpuNum = toNum(cpuRaw, 0);
  // Some APIs report CPU usage as 0..1 fraction
  const cpuPct = clamp(cpuNum > 0 && cpuNum <= 1 ? cpuNum * 100 : cpuNum);
  const ramUsed = toNum(
    stats.ram ?? stats.ram_usage ?? stats.memory_usage ?? stats.ram_used,
    0,
  );
  const ramTotal = toNum(
    stats.ram_total ?? stats.memory_total ?? stats.ramTotal,
    null,
  );
  const diskUsed = toNum(
    stats.disk ?? stats.disk_usage ?? stats.storage_usage ?? stats.disk_used,
    0,
  );
  const diskTotal = toNum(
    stats.disk_total ?? stats.storage_total ?? stats.diskTotal,
    null,
  );

  const usagePct = (used, total) =>
    typeof used === 'number' && typeof total === 'number' && total > 0
      ? clamp((used / total) * 100)
      : null;
  const ramPct = usagePct(ramUsed, ramTotal);
  const diskPct = usagePct(diskUsed, diskTotal);

  const formatRemaining = (date) => {
    const target = new Date(date);
    const ms = target.getTime() - Date.now();
    if (!Number.isFinite(ms) || ms <= 0) return 'Expired';
    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes - days * 60 * 24) / 60);
    const minutes = totalMinutes - days * 60 * 24 - hours * 60;
    const parts = [];
    if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'}`);
    if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
    parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
    return parts.join(', ');
  };

  const expiresText = formatRemaining(server.expiresAt);
  const statusText = displayStatus;
  const isActive = ['active', 'online', 'running'].includes(statusText.toLowerCase());

  return (
    <Layout>
      <div className="flex-1 flex flex-col justify-center min-h-[calc(100vh-120px)] py-6 w-full min-w-0 max-w-full overflow-x-hidden">
        <div className="space-y-4 sm:space-y-6 w-full min-w-0 max-w-full">
          <div className="flex flex-row items-center justify-between gap-3 min-w-0">
            <div className="min-w-0 shrink pr-2">
              <h1 className="text-xl sm:text-2xl md:text-3xl text-white truncate">My Server</h1>
            </div>
            <button
              onClick={() => navigate('/select-plan')}
              className="shrink-0 whitespace-nowrap w-auto px-4 py-2.5 sm:px-5 sm:py-3 rounded-[12px] text-white text-sm font-medium
              bg-[#FFFFFF0F]
              shadow-[0_2px_4px_#00000040,inset_0_1px_1px_#FFFFFF3B,inset_0_-16px_28px_#FFFFFF26]"
            >
              Order New Server
            </button>
          </div>

          <div className="border border-[#EBEBEB47] bg-[#FFFFFF21] rounded-xl p-4 sm:p-5 md:p-6 min-w-0 max-w-full overflow-hidden">
            <div className="grid min-w-0 grid-cols-1 min-[1400px]:grid-cols-3 gap-5 md:gap-6">
            {/* Device Usage */}
            <div className="min-w-0 flex flex-col gap-4">
              <h2 className="text-base sm:text-lg">Device Usage</h2>

              <div className="flex flex-1 flex-col gap-4">
              {[
                {
                  label: 'CPU',
                  valueText: Number.isFinite(cpuPct) ? `${Math.round(cpuPct)}%` : '—',
                  pct: Number.isFinite(cpuPct) ? cpuPct : 0,
                },
                {
                  label: 'RAM',
                  valueText:
                    typeof ramUsed === 'number' && typeof ramTotal === 'number'
                      ? `${ramUsed.toFixed(1)}/${ramTotal.toFixed(0)}GB`
                      : '—',
                  pct: typeof ramPct === 'number' ? ramPct : 0,
                },
                {
                  label: 'DISK',
                  valueText:
                    typeof diskUsed === 'number' && typeof diskTotal === 'number'
                      ? `${diskUsed.toFixed(0)}/${diskTotal.toFixed(0)}GB`
                      : '—',
                  pct: typeof diskPct === 'number' ? diskPct : 0,
                },
              ].map((row) => {
                const filled = Math.round((row.pct / 100) * 30);

                return (
                  <div
                    key={row.label}
                    className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-white/20 bg-white/10 px-3 py-3 sm:px-4 flex-1 flex flex-col justify-center"
                  >
                    <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                      <span className="shrink-0 text-base font-light text-white sm:text-lg md:text-xl">
                        {row.label}
                      </span>

                      <div className="min-w-0 flex-1 overflow-hidden ml-3 sm:ml-5">
                        <div className="flex items-end gap-[3px]">
                          {Array.from({ length: 30 }).map((_, i) => (
                            <div
                              key={i}
                              className={`w-[4px] shrink-0 rounded-full bg-[#FFFFFF36] h-7 sm:h-[45px] ${
                                i < filled ? 'myrdp-stick-fill' : ''
                              }`}
                              style={i < filled ? { animationDelay: `${i * 18}ms` } : undefined}
                            />
                          ))}
                        </div>
                      </div>

                      <span className="shrink-0 text-right text-base font-light text-white sm:text-lg md:text-xl">
                        {row.valueText}
                      </span>
                    </div>
                  </div>
                );
              })}
              </div>
            </div>

            {/* Login Data */}
            <div className="min-w-0 flex flex-col gap-4">
              <h2 className="text-base sm:text-lg">Login Data</h2>

              {[
                { label: 'IP:', value: displayIp },
                {
                  label: 'Username:',
                  value: displayUsername,
                },
                { label: 'Password:', value: mergedPassword || 'N/A' },
              ].map((item) => (
                (() => {
                  const key =
                    item.label === 'IP:'
                      ? 'ip'
                      : item.label === 'Username:'
                        ? 'username'
                        : 'password';
                  const isPassword = key === 'password';
                  const isCopied = copiedKey === key;
                  const displayValue =
                    isPassword && !showPassword ? '•'.repeat(Math.max(8, String(item.value || '').length || 8)) : item.value;

                  return (
                <div
                  key={item.label}
                  className={`font-geist min-w-0 max-w-full overflow-hidden rounded-2xl border border-[#EBEBEB47] bg-[#FFFFFF21] p-3 sm:p-4 transition flex-1 flex flex-col justify-center ${
                    isCopied ? 'opacity-70' : 'opacity-100'
                  }`}
                >
                  <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <div className="flex shrink-0 items-center gap-1">
                        <span className="myrdp-signal-dot h-3 w-3 rounded-full border border-[#009439]" />
                        <span className="myrdp-signal-dot h-3 w-3 rounded-full border border-[#009439]" />
                        <span className="myrdp-signal-dot h-3 w-3 rounded-full border border-[#009439]" />
                      </div>
                      <span className="shrink-0 text-sm text-white sm:text-base">{item.label}</span>
                    </div>
                    <div className="flex min-w-0 items-center gap-2 pl-1 sm:flex-1 sm:pl-0">
                      <code className="min-w-0 flex-1 break-all text-left text-xs text-white sm:text-sm sm:break-normal sm:truncate">
                        {displayValue}
                      </code>
                    <div className="shrink-0 flex items-center gap-2 ml-auto">
                      {isPassword ? (
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="rounded-xl border border-white/10 bg-white/5 p-2 text-white/80 hover:bg-white/10"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <path
                                d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                              <path d="M4 4l16 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <path
                                d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
                                stroke="currentColor"
                                strokeWidth="2"
                              />
                              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                            </svg>
                          )}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => copyToClipboard(key, String(item.value || ''))}
                        className={`rounded-xl border border-white/10 bg-white/5 p-2 text-white/80 hover:bg-white/10 transition ${
                          isCopied ? 'scale-[1.03] shadow-[0_10px_25px_rgba(72,111,255,0.25)]' : ''
                        }`}
                        aria-label={isCopied ? 'Copied' : 'Copy to clipboard'}
                      >
                        <FiCopy size={18} />
                      </button>
                      {isPassword ? (
                        <button
                          type="button"
                          onClick={() =>
                            openConfirmModal(
                              'Reset password',
                              'This will generate a new password on the provider. Your current password will stop working immediately.',
                              'reset-password'
                            )
                          }
                          disabled={passwordResetLoading || actionLoading}
                          className="rounded-xl border border-white/10 bg-white/5 p-2 text-white/80 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label="Reset password"
                          title="Reset password"
                        >
                          <FiRefreshCw
                            size={18}
                            className={passwordResetLoading ? 'animate-spin' : ''}
                          />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  </div>
                </div>
                  );
                })()
              ))}
            </div>

            {/* Plan Card */}
            <div className="relative min-w-0 max-w-full overflow-hidden rounded-2xl bg-[#FFFFFF14] border border-[#EBEBEB1C] p-4 sm:p-5">
              <img
                src={worldMap}
                alt="world map"
                className={`pointer-events-none absolute top-10 w-auto hidden sm:block
                ${server.location === "nl" ? "-right-24" : "-right-60"}`}
              />

              <div
                className={`absolute hidden sm:flex
                items-center gap-3 bg-[#FFFFFF1A] py-2 px-4 rounded-xl backdrop-blur-[4px]
                shadow-[0_2px_4px_#00000040,inset_0_1px_1px_#FFFFFF3B,inset_0_-16px_28px_#FFFFFF26]
                ${server.location === "nl" ? "top-[35%] right-8" : "top-[40%] right-8"}`}
              >
                <div className="rounded-full border border-[#C0C0C0] flex items-center justify-center overflow-hidden">
                  <img
                    src={server.location === "nl" ? nl : usa}
                    alt={server.location}
                    className="h-5 w-5 object-cover"
                  />
                </div>

                <p className="text-xl text-white font-medium">
                  {server.location === "nl" ? "NL" : "USA"}
                </p>
              </div>          

              <div className="relative z-10">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-xs text-white/60 truncate">
                      {server.plan}
                    </div>
                    <div className='bg-[#03061433] rounded-xl'>
                      <div className="mt-2 flex items-end gap-2 bg-no-repeat bg-cover p-4 rounded-xl border border-[#EBEBEB1C]"
                        style={{ backgroundImage: `url(${priceBG})` }}   
                      >
                        <div className="text-4xl sm:text-5xl text-white leading-none">
                          {(() => { const p = toNum(server.monthlyPrice, 0); return p % 1 !== 0 ? p.toFixed(2) : String(Math.round(p)); })()}€
                        </div>
                        <div className="pb-1 text-sm text-white/60">/monthly</div>
                      </div>                      
                    </div>
                  </div>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                      isActive
                        ? 'border-emerald-500/45 bg-emerald-500/15 text-emerald-200'
                        : 'border-white/20 bg-white/10 text-white/75'
                    }`}
                  >
                    {isActive ? 'Active' : statusText}
                  </span>
                </div>

                <div className='flex justify-between md:items-end flex-col md:flex-row gap-2'>
                  <div className="mt-4 space-y-2 text-sm text-white/80">
                    <div className="flex items-center gap-2">
                      <div className="myrdp-plan-icon p-1 rounded-lg bg-[#0B3BF0]" style={{ '--flow-delay': '0ms' }}>
                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.5306 8.52098C10.5306 9.46674 10.5306 9.93874 10.2372 10.2328C9.94298 10.5261 9.4699 10.5261 8.52455 10.5261H5.8498C4.90445 10.5261 4.43137 10.5261 4.13715 10.2328C3.84376 9.93874 3.84375 9.46674 3.84375 8.52098V5.84745C3.84375 4.90252 3.84376 4.42962 4.13715 4.13636C4.43137 3.84228 4.90445 3.84229 5.8498 3.84229H8.52455C9.4699 3.84229 9.94298 3.84228 10.2372 4.13636C10.5306 4.42962 10.5306 4.90252 10.5306 5.84745" stroke="white" strokeLinecap="round"/>
                          <path d="M2.17188 7.18424C2.17188 4.82152 2.17187 3.64018 2.90659 2.90579C3.64047 2.17141 4.82321 2.17139 7.18701 2.17139C9.55081 2.17139 10.7335 2.17141 11.4674 2.90579C12.2021 3.64018 12.2021 4.82152 12.2021 7.18424C12.2021 9.5478 12.2021 10.7292 11.4674 11.4627C10.7335 12.1971 9.55081 12.1971 7.18701 12.1971C4.82321 12.1971 3.64047 12.1971 2.90659 11.4627C2.17187 10.7292 2.17188 9.5478 2.17188 7.18424Z" stroke="white"/>
                          <path d="M2.17171 7.18408H0.5" stroke="white" strokeLinecap="round"/>
                          <path d="M13.8739 7.18408H12.2021" stroke="white" strokeLinecap="round"/>
                          <path d="M2.17171 5.51318H0.5" stroke="white" strokeLinecap="round"/>
                          <path d="M13.8739 5.51318H12.2021" stroke="white" strokeLinecap="round"/>
                          <path d="M2.17171 8.85498H0.5" stroke="white" strokeLinecap="round"/>
                          <path d="M13.8739 8.85498H12.2021" stroke="white" strokeLinecap="round"/>
                          <path d="M7.18652 12.1968V13.8677" stroke="white" strokeLinecap="round"/>
                          <path d="M7.18652 0.5V2.17095" stroke="white" strokeLinecap="round"/>
                          <path d="M5.51562 12.1968V13.8677" stroke="white" strokeLinecap="round"/>
                          <path d="M5.51562 0.5V2.17095" stroke="white" strokeLinecap="round"/>
                          <path d="M8.8584 12.1968V13.8677" stroke="white" strokeLinecap="round"/>
                          <path d="M8.8584 0.5V2.17095" stroke="white" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <span>{server.specs?.cores || 0} Cores</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="myrdp-plan-icon p-1 rounded-lg bg-[#0B3BF0]" style={{ '--flow-delay': '160ms' }}>
                        <svg width="14" height="9" viewBox="0 0 14 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.1271 0.0292702C13.0786 0.0100543 13.0268 0 12.9742 0L0.398695 0.0501265C0.292541 0.050962 0.191417 0.0919007 0.11619 0.165423C0.0417991 0.238944 0 0.338361 0 0.44196L0.0150499 6.87432C0.0158857 7.08987 0.194762 7.26529 0.415428 7.26696L1.43599 7.27196V7.96374C1.43599 8.18013 1.61404 8.35476 1.83471 8.35476H3.53065C3.53065 8.35476 3.9511 7.35386 5.10291 7.35386C6.25555 7.35386 6.68685 8.35476 6.68685 8.35476H11.6159C11.6685 8.35476 11.7204 8.34472 11.7688 8.32467C11.8173 8.30545 11.8616 8.27625 11.8984 8.24033C11.936 8.2044 11.9653 8.16092 11.9853 8.1133C12.0054 8.06651 12.0154 8.01554 12.0154 7.96374V7.43325H12.9733C13.026 7.43325 13.0778 7.42322 13.1263 7.40317C13.1748 7.38395 13.219 7.35554 13.2567 7.31877C13.2934 7.28285 13.3227 7.23942 13.3436 7.1918C13.3637 7.14501 13.3737 7.09404 13.3737 7.04224V0.389334C13.3737 0.337535 13.3637 0.287393 13.3436 0.239771C13.3235 0.192984 13.2943 0.149539 13.2567 0.113613C13.2199 0.0776878 13.1756 0.0484861 13.1271 0.0292702ZM1.04984 1.12874C1.07492 1.10451 1.10833 1.09029 1.14428 1.09029H4.07311C4.09067 1.09029 4.10822 1.09365 4.1241 1.10034C4.14082 1.10702 4.15503 1.11621 4.16757 1.12874C4.18011 1.14044 4.19014 1.15463 4.19683 1.1705C4.20352 1.18638 4.20685 1.20392 4.20685 1.22063L4.19767 5.77398C4.19767 5.77398 3.9235 5.78316 3.68778 5.93522C3.45207 6.08728 3.39191 6.29534 3.39191 6.29534H1.01056V1.22063C1.01056 1.18554 1.02477 1.15297 1.04984 1.12874ZM5.17813 1.12874C5.20321 1.10451 5.23664 1.09029 5.27175 1.09029H8.20143C8.22817 1.09029 8.25408 1.09781 8.27581 1.11201C8.29754 1.12622 8.31427 1.14711 8.3243 1.1705C8.33099 1.18638 8.33432 1.20308 8.33432 1.22063L8.32512 6.165C8.32512 6.20009 8.31091 6.23264 8.28583 6.2577C8.26076 6.28193 8.22648 6.29534 8.19138 6.29534H6.84732C6.84732 6.29534 6.52553 6.0054 6.1402 5.87423C5.75404 5.74306 5.13885 5.71468 5.13885 5.71468V1.21981C5.13885 1.18556 5.15306 1.15297 5.17813 1.12874ZM9.30642 1.12874C9.33066 1.10451 9.36493 1.09029 9.40003 1.09029H12.3297C12.3565 1.09029 12.3815 1.09781 12.4041 1.11201C12.4258 1.12622 12.4426 1.14711 12.4526 1.1705C12.4593 1.18638 12.4626 1.20308 12.4626 1.22063L12.4534 6.165C12.4534 6.20009 12.4392 6.23266 12.4141 6.25689C12.389 6.28112 12.3556 6.29534 12.3205 6.29534H9.26714V1.22063C9.26714 1.18554 9.28134 1.15297 9.30642 1.12874Z" fill="white"/>
                        </svg>

                      </div>
                      <span>{server.specs?.ram || 0} GB DDR5 RAM</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="myrdp-plan-icon p-1 rounded-lg bg-[#0B3BF0]" style={{ '--flow-delay': '320ms' }}>
                        <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10.3512 1.35178L10.7708 1.25321L10.77 1.24903L10.7683 1.24572L10.3512 1.35178ZM2.1866 1.35178L1.76951 1.24572L1.76783 1.24903L1.76701 1.25321L2.1866 1.35178ZM11.5942 6.08647C11.661 6.27445 11.9034 6.38223 12.1358 6.32792C12.3673 6.27362 12.5011 6.07724 12.4342 5.89009L11.5942 6.08647ZM0.103643 5.89009C0.0367744 6.07724 0.170507 6.27362 0.402039 6.32792C0.634407 6.38223 0.876813 6.27445 0.943682 6.08647L0.103643 5.89009ZM11.6025 3.57168C11.5357 3.3837 11.2924 3.27592 11.0609 3.33022C10.8285 3.38453 10.6948 3.58086 10.7625 3.768L11.6025 3.57168ZM10.1389 2.02938C10.2058 2.21737 10.449 2.32514 10.6806 2.27084C10.9129 2.21653 11.0458 2.02021 10.979 1.83306L10.1389 2.02938ZM6.26892 8.48341C6.02736 8.48341 5.83176 8.64135 5.83176 8.83685C5.83176 9.03235 6.02736 9.19023 6.26892 9.19023V8.48341ZM3.93606 9.19023C4.17762 9.19023 4.37404 9.03235 4.37404 8.83685C4.37404 8.64135 4.17762 8.48341 3.93606 8.48341V9.19023ZM3.64432 0.706819H8.89352V0H3.64432V0.706819ZM8.89352 0.706819C9.1426 0.706819 9.33232 0.754439 9.48528 0.850519C9.64243 0.94994 9.80543 1.12872 9.93415 1.4579L10.7683 1.24572C10.6045 0.826309 10.3613 0.506309 10.0227 0.293263C9.68088 0.0777103 9.28804 0 8.89352 0V0.706819ZM3.64432 0C3.25063 0 2.85695 0.0777103 2.51425 0.293263C2.17657 0.506309 1.93334 0.826309 1.76951 1.24572L2.60368 1.4579C2.73241 1.12872 2.89541 0.94994 3.05255 0.850519C3.20552 0.754439 3.39524 0.706819 3.64432 0.706819V0ZM1.76701 1.25321L0.103643 5.89009L0.943682 6.08647L2.60703 1.4504L1.76701 1.25321ZM2.47831 5.69713H10.0595V4.99031H2.47831V5.69713ZM2.47831 8.48341C2.05955 8.48341 1.77954 8.48257 1.56723 8.46419C1.36328 8.44664 1.27052 8.41577 1.20866 8.37984L0.70296 8.95714C0.935328 9.08998 1.19444 9.14263 1.47362 9.16769C1.7436 9.19109 2.07877 9.19023 2.47831 9.19023V8.48341ZM2.62863e-06 7.09068C2.62863e-06 7.43406 -0.000822412 7.71645 0.0267608 7.9437C0.0543441 8.1768 0.113687 8.38987 0.264141 8.58203L1.00386 8.20438C0.957048 8.14506 0.918612 8.05731 0.897716 7.87685C0.875148 7.69053 0.875141 7.44743 0.875141 7.09068H2.62863e-06ZM1.20866 8.37984C1.12926 8.33473 1.05902 8.27539 1.00386 8.20438L0.264141 8.58203C0.378653 8.72908 0.528266 8.85688 0.70296 8.95714L1.20866 8.37984ZM11.6627 7.09068C11.6627 7.44743 11.6627 7.69053 11.6401 7.87685C11.6192 8.05731 11.5808 8.14506 11.534 8.20438L12.2737 8.58203C12.4242 8.38987 12.4835 8.1768 12.5111 7.9437C12.5387 7.71645 12.5378 7.43406 12.5378 7.09068H11.6627ZM10.0595 9.19023C10.4591 9.19023 10.7942 9.19109 11.0642 9.16769C11.3434 9.14263 11.6025 9.08998 11.8349 8.95714L11.3292 8.37984C11.2673 8.41577 11.1746 8.44664 10.9706 8.46419C10.7583 8.48257 10.4783 8.48341 10.0595 8.48341V9.19023ZM11.534 8.20438C11.4788 8.27539 11.4094 8.33473 11.3292 8.37984L11.8349 8.95714C12.0096 8.85688 12.1592 8.72908 12.2737 8.58203L11.534 8.20438ZM10.0595 5.69713C10.4783 5.69713 10.7583 5.69793 10.9706 5.71631C11.1746 5.73385 11.2673 5.76478 11.3292 5.8007L11.8349 5.2234C11.6025 5.09056 11.3434 5.03795 11.0642 5.01372C10.7942 4.98949 10.4591 4.99031 10.0595 4.99031V5.69713ZM12.5378 7.09068C12.5378 6.74646 12.5387 6.4641 12.5111 6.23685C12.4835 6.00459 12.4242 5.79067 12.2737 5.59851L11.534 5.97617C11.5808 6.03549 11.6192 6.12322 11.6401 6.30452C11.6627 6.48999 11.6627 6.7331 11.6627 7.09068H12.5378ZM11.3292 5.8007C11.4094 5.84582 11.4788 5.90515 11.534 5.97617L12.2737 5.59851C12.1592 5.45147 12.0096 5.32366 11.8349 5.2234L11.3292 5.8007ZM2.47831 4.99031C2.07877 4.99031 1.7436 4.98949 1.47362 5.01372C1.19444 5.03795 0.935328 5.09056 0.70296 5.2234L1.20866 5.8007C1.27052 5.76478 1.36328 5.73385 1.56723 5.71631C1.77954 5.69793 2.05955 5.69713 2.47831 5.69713V4.99031ZM0.875141 7.09068C0.875141 6.7331 0.875148 6.48999 0.897716 6.30452C0.918612 6.12322 0.957048 6.03549 1.00386 5.97617L0.264141 5.59851C0.113687 5.79067 0.0543441 6.00459 0.0267608 6.23685C-0.000822412 6.4641 2.62863e-06 6.74646 2.62863e-06 7.09068H0.875141ZM0.70296 5.2234C0.528266 5.32366 0.378653 5.45147 0.264141 5.59851L1.00386 5.97617C1.05902 5.90515 1.12926 5.84582 1.20866 5.8007L0.70296 5.2234ZM10.7625 3.768L11.5942 6.08647L12.4342 5.89009L11.6025 3.57168L10.7625 3.768ZM9.93081 1.4504L10.1389 2.02938L10.979 1.83306L10.7708 1.25321L9.93081 1.4504ZM10.0595 8.48341H6.26892V9.19023H10.0595V8.48341ZM3.93606 8.48341H2.47831V9.19023H3.93606V8.48341Z" fill="white"/>
                        </svg>

                      </div>
                      <span>{server.specs?.storage || 0} GB NVMe</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="myrdp-plan-icon p-1 rounded-lg bg-[#0B3BF0]" style={{ '--flow-delay': '480ms' }}>
                        <svg width="15" height="15" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M0 3.08627L0.917763 2.18226C2.33705 0.785347 4.26203 0 6.26892 0C8.27581 0 10.2008 0.785347 11.6201 2.18226L12.5378 3.08627L11.4295 4.17738L10.5117 3.27342C9.38665 2.16558 7.86039 1.54311 6.26892 1.54311C4.67745 1.54311 3.15119 2.16558 2.02612 3.27342L1.10834 4.17738L0 3.08627Z" fill="white"/>
                          <path d="M3.53674 6.6837L2.50781 5.57L3.35956 4.64682C4.13189 3.81134 5.17837 3.3418 6.26916 3.3418C7.35995 3.3418 8.40644 3.81134 9.17877 4.64682L10.0305 5.57L9.00159 6.6837L8.14984 5.76133C7.65083 5.22078 6.97463 4.91749 6.26916 4.91749C5.5637 4.91749 4.88749 5.22078 4.38849 5.76133L3.53674 6.6837Z" fill="white"/>
                          <path d="M6.26933 10.0255L4.17969 8.06796L5.22451 7.08879C5.50201 6.82979 5.87731 6.68359 6.26933 6.68359C6.66134 6.68359 7.03748 6.82979 7.31415 7.08879L8.35897 8.06796L6.26933 10.0255Z" fill="white"/>
                        </svg>
                      </div>
                      <span>{server.specs?.uplink || '10 Gbit Uplink'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="myrdp-plan-icon p-1 rounded-lg bg-[#0B3BF0]" style={{ '--flow-delay': '640ms' }}>
                        <svg width="15" height="15" viewBox="0 0 13 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M0.599609 5.61295C0.599609 4.95293 0.740029 4.30121 1.01335 3.69132C1.28584 3.08977 1.68621 2.52995 2.19107 2.07044C2.69509 1.60257 3.29441 1.23503 3.9539 0.984384C4.61255 0.733741 5.31885 0.600098 6.03267 0.600098C6.74649 0.600098 7.45279 0.733741 8.11144 0.984384C8.77093 1.23503 9.37025 1.60257 9.87427 2.07044C10.3791 2.52995 10.7795 3.08977 11.052 3.69132C11.3253 4.30121 11.4657 4.95293 11.4657 5.61295" stroke="white" strokeWidth="1.2" strokeLinejoin="round"/>
                          <path d="M4.7793 7.28377C4.7793 6.95794 4.82277 6.63208 4.90635 6.32295C4.99078 6.02218 5.11364 5.7465 5.26911 5.51257C5.42458 5.27863 5.60845 5.09481 5.81157 4.96949C6.01384 4.84417 6.23118 4.77734 6.45101 4.77734C6.67084 4.77734 6.88817 4.84417 7.09045 4.96949C7.29356 5.09481 7.47744 5.27863 7.63291 5.51257C7.78838 5.7465 7.91124 6.02218 7.99566 6.32295C8.07925 6.63208 8.12272 6.95794 8.12272 7.28377" stroke="white" strokeWidth="1.2" strokeLinejoin="round"/>
                          <path d="M7.28613 4.77737L8.12199 3.94189" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M11.4657 5.61279V6.44827C11.4657 6.90778 11.1615 7.28374 10.787 7.28374H1.27915C0.903851 7.28374 0.599609 6.90778 0.599609 6.44827V5.61279" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="leading-snug">{(() => {
                        const cpu = server.specs?.cpu || '4.5 GHz Base (5.7 GHz Turbo)';
                        const m = cpu.match(/^(.+?)(\s*\(.+\))$/);
                        if (m) return <>{m[1]}<br /><span className="text-white/50">{m[2].trim()}</span></>;
                        return cpu;
                      })()}</span>
                    </div>                    
                  </div>

                  <div className="mt-2 flex flex-col items-start gap-3">
                    <span className="text-xs text-white font-semibold">Expires in</span>
                    <span className="rounded-full border border-[#009439] bg-[#0094399C] px-3 py-1 text-xs text-white">
                      {expiresText}
                    </span>
                  </div>                  
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 sm:mt-6 rounded-2xl border border-[#EBEBEB1C] bg-[#FFFFFF14] p-4 sm:p-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {isStopped ? (
                <button
                  onClick={() =>
                    openConfirmModal(
                      'Confirm Start',
                      'Are you sure you want to start this server?',
                      'server-action',
                      { action: 'start', data: {} },
                    )
                  }
                  disabled={actionLoading}
                  className="rdp-outline-btn flex min-h-[64px] w-full items-center justify-center gap-2 px-4 py-4 text-center"
                >
                  <FiPower size={18} />
                  <span className="text-base sm:text-lg">Start Server</span>
                </button>
              ) : (
                <button
                  onClick={() =>
                    openConfirmModal(
                      'Confirm Shutdown',
                      'Are you sure you want to shutdown this server?',
                      'server-action',
                      { action: 'shutdown', data: {} },
                    )
                  }
                  disabled={actionLoading}
                  className="rdp-outline-btn flex min-h-[64px] w-full items-center justify-center gap-2 px-4 py-4 text-center"
                >
                  <img
                    src={shutdown}
                    alt="shutdown"
                    className="h-[18px] w-[18px] object-contain"
                  />
                  <span className="text-base sm:text-lg">Shutdown Server</span>
                </button>
              )}

              <button
                onClick={() =>
                  openConfirmModal(
                    'Confirm Restart',
                    'Are you sure you want to restart this server?',
                    'server-action',
                    { action: 'restart', data: {} },
                  )
                }
                disabled={actionLoading}
                className="rdp-outline-btn flex min-h-[64px] w-full items-center justify-center gap-2 px-4 py-4 text-center"
              >
                <img
                  src={restart}
                  alt="restart"
                  className="h-[18px] w-[18px] object-contain"
                />
                <span className="text-base sm:text-lg">Restart Server</span>
              </button>

              <button
                onClick={() => setShowExtendModal(true)}
                disabled={actionLoading}
                className="rdp-outline-btn flex min-h-[64px] w-full items-center justify-center gap-2 px-4 py-4 text-center"
              >
                <img
                  src={extend}
                  alt="extend"
                  className="h-[18px] w-[18px] object-contain"
                />
                <span className="text-base sm:text-lg">Extend Time</span>
              </button>

              <button
                onClick={() => setShowUpgradeModal(true)}
                disabled={actionLoading}
                className="rdp-outline-btn flex min-h-[64px] w-full items-center justify-center gap-2 px-4 py-4 text-center"
              >
                <img
                  src={upgrade}
                  alt="upgrade"
                  className="h-[18px] w-[18px] object-contain"
                />
                <span className="text-base sm:text-lg">Upgrade Server</span>
              </button>

              <button
                onClick={() => setShowReinstallModal(true)}
                disabled={actionLoading}
                className="rdp-outline-btn flex min-h-[64px] w-full items-center justify-center gap-2 px-4 py-4 text-center"
              >
                <img
                  src={reinstallation}
                  alt="reinstallation"
                  className="h-[18px] w-[18px] object-contain"
                />
                <span className="text-base sm:text-lg">Reinstallation</span>
              </button>

              <div className="rdp-outline-btn flex min-h-[64px] w-full items-center justify-center px-4 py-4">
                <div className="flex w-full flex-wrap items-center justify-center gap-2 text-base text-white sm:gap-3 sm:text-lg">
                  <span className="font-medium whitespace-nowrap">
                    Auto-Renew
                  </span>

                  <span className="text-white/60">|</span>

                  <span
                    className={`whitespace-nowrap ${!server.autoRenew ? 'text-white' : 'text-white/40'}`}
                  >
                    OFF
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      openConfirmModal(
                        'Confirm Auto-Renew Change',
                        `Are you sure you want to ${
                          server.autoRenew ? 'disable' : 'enable'
                        } auto-renew?`,
                        'toggle-auto-renew',
                        {
                          action: server.autoRenew ? 'disable' : 'enable',
                          data: { autoRenew: !server.autoRenew },
                        },
                      )
                    }
                    className="relative shrink-0 rounded-full border-2 border-[#0B3BF0] bg-transparent transition-all duration-300
                       h-[34px] w-[64px] sm:h-[36px] sm:w-[70px]"
                  >
                    <span
                      className={`absolute top-1/2 -translate-y-1/2 rounded-full transition-all duration-300
                          h-[24px] w-[24px] sm:h-[26px] sm:w-[26px] bg-[#0B3BF0]
                          ${server.autoRenew ? 'left-[34px] sm:left-[38px]' : 'left-[4px]'}`}
                    />
                  </button>

                  <span
                    className={`whitespace-nowrap ${server.autoRenew ? 'text-white' : 'text-white/40'}`}
                  >
                    ON
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Extend Modal */}
      {showExtendModal &&
        createPortal(
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm animate-enterUp"
            onMouseDown={() => setShowExtendModal(false)}
            role="presentation"
          >
            <div className="myrdp-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Extend</h2>
              <button
                onClick={() => setShowExtendModal(false)}
                className="text-white/70 hover:text-white"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-white/70 mb-2">
                Duration of days
              </label>
              <select
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value))}
                className="rdp-input"
              >
                {[30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360].map(
                  (days) => (
                    <option key={days} value={days}>
                      {days} days
                    </option>
                  ),
                )}
              </select>
            </div>

            <p className="text-white/80 mb-6">
              Extending the Server by {extendDays} days, will deduct {extendPrice.toFixed(2)}€ from your Balance
            </p>

            <button
              onClick={() =>
                openConfirmModal(
                  'Confirm Extend',
                  `Are you sure you want to extend this server by ${extendDays} days?`,
                  'extend',
                )
              }
              disabled={actionLoading || (user && user.balance < extendPrice)}
              className="w-full rdp-primary-btn py-3"
            >
              {actionLoading ? 'Processing...' : 'Extend'}
            </button>
            </div>
          </div>,
          document.body,
        )}

      {/* Reinstall Modal */}
      {showReinstallModal &&
        createPortal(
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm animate-enterUp"
            onMouseDown={() => setShowReinstallModal(false)}
            role="presentation"
          >
            <div className="myrdp-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Reinstall Server</h2>
              <button
                onClick={() => setShowReinstallModal(false)}
                className="text-white/70 hover:text-white"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-sm text-white/70 mb-2">
                Operating System
              </label>
              <select
                value={selectedOsId}
                onChange={(e) => setSelectedOsId(e.target.value)}
                className="rdp-input"
              >
                <option value="">Select OS...</option>
                {osTemplates.map((os) => (
                  <option key={os.id} value={os.id}>
                    {os.name}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={() =>
                openConfirmModal(
                  'Confirm Reinstall',
                  'Reinstall will erase all data on this server. Do you want to continue?',
                  'reinstall',
                )
              }
              disabled={actionLoading || !selectedOsId}
              className="w-full rdp-primary-btn py-3"
            >
              {actionLoading ? 'Processing...' : 'Reinstall'}
            </button>
            </div>
          </div>,
          document.body,
        )}

      {/* Upgrade Modal */}
      {showUpgradeModal &&
        createPortal(
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm animate-enterUp"
            onMouseDown={() => setShowUpgradeModal(false)}
            role="presentation"
          >
            <div className="myrdp-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Upgrade Server</h2>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-white/70 hover:text-white"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-white/70 mb-2">
                Target Package
              </label>
              <select
                value={selectedUpgradePlanId}
                onChange={(e) => setSelectedUpgradePlanId(e.target.value)}
                className="rdp-input"
              >
                <option value="">Select package...</option>
                {upgradeTiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name} - {formatTierMonthlyPriceDisplay(tier)}/month
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm text-white/70 mb-2">
                Duration (days)
              </label>
              <select
                value={upgradeDays}
                onChange={(e) => setUpgradeDays(parseInt(e.target.value, 10))}
                className="rdp-input"
              >
                {[30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360].map(
                  (days) => (
                    <option key={days} value={days}>
                      {days} days
                    </option>
                  ),
                )}
              </select>
            </div>

            <p className="text-white/80 mb-6">
              Upgrade cost for {upgradeDays} days: {upgradePrice.toFixed(2)}€
            </p>

            <button
              onClick={() =>
                openConfirmModal(
                  'Confirm Upgrade',
                  `Are you sure you want to upgrade this server for ${upgradeDays} days?`,
                  'upgrade',
                )
              }
              disabled={
                actionLoading ||
                !selectedUpgradePlanId ||
                (user && user.balance < upgradePrice)
              }
              className="w-full rdp-primary-btn py-3"
            >
              {actionLoading ? 'Processing...' : 'Upgrade'}
            </button>
            </div>
          </div>,
          document.body,
        )}

      {/* Global Confirmation Modal */}
      {confirmModal.open &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/75 p-4 backdrop-blur-md animate-enterUp"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !(confirmModal.type === 'reset-password' && passwordResetLoading)) {
                closeConfirmModal();
              }
            }}
            role="presentation"
          >
            <div
              className="relative myrdp-modal max-w-md border border-[#EBEBEB1C] bg-[#212A5533] pt-5 shadow-[0_24px_60px_rgba(0,0,0,0.55),inset_0_1px_0_rgba(255,255,255,0.06)]"
              role="dialog"
              aria-modal="true"
              aria-labelledby="confirm-modal-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={closeConfirmModal}
                className="absolute right-3 top-3 z-10 rounded-xl border border-white/10 bg-[#0d1229]/90 p-2 text-white/70 backdrop-blur-sm transition hover:bg-white/10 hover:text-white sm:right-4 sm:top-4"
                aria-label="Close"
              >
                <FiX size={20} />
              </button>

              {confirmModal.type === 'reset-password' && (
                <div className="mb-5 flex justify-center">
                  <div className="rounded-2xl border border-[#486FFF]/35 bg-[#486FFF]/12 p-4 shadow-[0_0_32px_rgba(72,111,255,0.2)]">
                    <FiRefreshCw className="text-[#93b4ff]" size={28} aria-hidden />
                  </div>
                </div>
              )}

              <div className="mb-4 px-1 pr-12 sm:pr-14">
                <h2 id="confirm-modal-title" className="text-xl font-semibold tracking-tight text-white">
                  {confirmModal.title}
                </h2>
              </div>

              <p className="mb-6 text-sm leading-relaxed text-[#c8d0e0]/90">{confirmModal.message}</p>

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={closeConfirmModal}
                  disabled={confirmModal.type === 'reset-password' && passwordResetLoading}
                  className="w-full rounded-xl border border-white/15 bg-[#FFFFFF08] py-3 text-sm font-semibold text-white transition hover:bg-white/10 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmedAction}
                  disabled={
                    actionLoading ||
                    (confirmModal.type === 'reset-password' && passwordResetLoading)
                  }
                  className="w-full rounded-xl bg-gradient-to-b from-[#3b5cff] to-[#2333FF] py-3 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(35,51,255,0.35)] transition hover:brightness-105 disabled:opacity-50"
                >
                  {confirmModal.type === 'reset-password' && passwordResetLoading
                    ? 'Resetting...'
                    : actionLoading
                      ? 'Processing...'
                      : confirmModal.type === 'reset-password'
                        ? 'Reset password'
                        : 'Confirm'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </Layout>
  );
};

export default ServerManagement;
